import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, Globe, Zap, Users, Calendar, Music, Mic, FileText, Hammer, PartyPopper } from 'lucide-react'
import { Button } from '@/components/ui'
import { useSEO } from '@/hooks/useSEO'
import { getUtmContentSlug, buildGetStartedUrl } from '@/lib/utm-utils'
import { trackGetStartedClick, trackCalendlyClick } from '@/lib/analytics'

const CALENDLY_BASE = '/book'

/** True when the visitor just completed a Stripe deposit (success_url ?paid=1). */
function useJustPaid(): boolean {
  return useMemo(() => {
    if (typeof window === 'undefined') return false
    return new URLSearchParams(window.location.search).get('paid') === '1'
  }, [])
}

/**
 * Post-payment success state. The pay-link Checkout success_url lands a brand-new
 * client here. The journey was preview-first/trust-first up to this point, so the
 * pay moment should read as JOINING, not a receipt: welcome them, then set the
 * three onboarding expectations (the full welcome packet follows from /onboard).
 */
function PaidWelcome() {
  useSEO({
    title: "Welcome to Fugue Lab | You're in",
    description: 'Your deposit is in and your project is officially underway.',
    url: '/welcome',
  })

  const steps = [
    {
      icon: FileText,
      title: 'A welcome packet, shortly',
      desc: "We'll email your content questionnaire and a quick onboarding guide within a business day.",
    },
    {
      icon: Calendar,
      title: 'Your kickoff call',
      desc: 'We walk your pages, audience, and timeline together, then I start building.',
    },
    {
      icon: Hammer,
      title: 'We build, you review',
      desc: 'Mockup to staging to launch, with your feedback at every step. Most builds run 4 to 6 weeks.',
    },
  ]

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border/50 border-b px-6 py-4">
        <Link to="/" className="text-lg font-semibold tracking-tight">
          Fugue Lab
        </Link>
      </header>

      <main className="mx-auto max-w-2xl space-y-12 px-6 py-20">
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-5 text-center"
        >
          <PartyPopper className="text-primary mx-auto h-10 w-10" />
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Welcome to Fugue Lab.</h1>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Your deposit is in and your project is officially underway. Here's what happens next.
          </p>
        </motion.section>

        <section className="space-y-4">
          {steps.map(({ icon: Icon, title, desc }, i) => (
            <div key={title} className="border-border/50 flex gap-4 rounded-xl border p-5">
              <div className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                {i + 1}
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Icon className="text-primary h-4 w-4" />
                  <h3 className="font-semibold">{title}</h3>
                </div>
                <p className="text-muted-foreground text-sm">{desc}</p>
              </div>
            </div>
          ))}
        </section>

        <section className="border-border/50 space-y-4 border-t py-8 text-center">
          <p className="text-muted-foreground text-sm">
            A receipt is on its way from Stripe. Questions in the meantime?
          </p>
          <Button variant="outline" asChild>
            <Link to="/contact">Get in touch</Link>
          </Button>
        </section>
      </main>
    </div>
  )
}

/**
 * Build a Calendly URL that carries the lead's UTM attribution forward.
 * The outreach Lambda lands prospects on /welcome with utm_term={leadId} and
 * utm_content={orgType}. We forward those to Calendly so the eventual booking
 * webhook can match the booking back to the lead in the CRM.
 */
function useCalendlyUrl(): string {
  return useMemo(() => {
    if (typeof window === 'undefined') return CALENDLY_BASE
    const params = new URLSearchParams(window.location.search)
    const forwarded = new URLSearchParams()
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
      const value = params.get(key)
      if (value) forwarded.set(key, value)
    }
    const qs = forwarded.toString()
    return qs ? `${CALENDLY_BASE}?${qs}` : CALENDLY_BASE
  }, [])
}

// ---------------------------------------------------------------------------
// Personalized content based on UTM org type
// ---------------------------------------------------------------------------

interface PersonalizedContent {
  headline: string
  subHeadline: string
  /** Which case study slug to show first (moved to index 0). */
  featuredSlug: string | null
  cards: { icon: typeof Globe; title: string; desc: string }[]
}

const DEFAULT_CARDS = [
  { icon: Globe, title: 'Modern Websites', desc: 'Fast, mobile-first sites with a CMS you can actually use.' },
  { icon: Zap, title: 'Connect Your Tools', desc: 'Eventbrite, Mailchimp, PayPal, Submittable. One front door.' },
  { icon: Users, title: 'Built for Music', desc: 'Concert calendars, donor pages, application funnels.' },
]

const CONTENT_GROUPS: Record<string, PersonalizedContent> = {
  // Church
  'church-music-program': {
    headline: 'Your congregation finds your music program online. What do they see?',
    subHeadline:
      'Fugue Lab builds websites for church music programs that connect your service schedule, choir information, and donor pages into one place your congregation can actually find. Built by a working organist and choirmaster.',
    featuredSlug: 'holy-trinity-covina',
    cards: [
      {
        icon: Calendar,
        title: 'Service Schedules',
        desc: 'Publish your service calendar automatically from your planning tools.',
      },
      { icon: Music, title: 'Choir & Music', desc: 'Choir roster, rehearsal info, and upcoming anthems in one place.' },
      {
        icon: Users,
        title: 'Donor Pages',
        desc: 'Year-end appeals, giving levels, and stewardship tied to the music year.',
      },
    ],
  },
  // Ensembles
  'community-choir': {
    headline: 'Your season is set. Now make it findable.',
    subHeadline:
      'Fugue Lab builds websites for community ensembles that turn a concert season into a shareable, ticketed, audience-building engine. Built by a conductor who knows what season announcement week actually looks like.',
    featuredSlug: 'chroma-music-festival',
    cards: DEFAULT_CARDS,
  },
  'community-orchestra': {
    headline: 'Your season is set. Now make it findable.',
    subHeadline:
      'Fugue Lab builds websites for community orchestras that connect your season calendar, donor tools, and subscription management into one professional front door.',
    featuredSlug: 'chroma-music-festival',
    cards: DEFAULT_CARDS,
  },
  'youth-orchestra': {
    headline: 'Your season is set. Now make it findable.',
    subHeadline:
      'Fugue Lab builds websites for youth orchestras that connect audition forms, parent communication, and concert schedules into one place families can trust.',
    featuredSlug: 'chroma-music-festival',
    cards: DEFAULT_CARDS,
  },
  'chamber-ensemble': {
    headline: 'Your season is set. Now make it findable.',
    subHeadline:
      'Fugue Lab builds websites for chamber ensembles that turn your concert calendar, booking inquiries, and mailing list into one professional presence.',
    featuredSlug: 'chroma-music-festival',
    cards: DEFAULT_CARDS,
  },
  'jazz-ensemble': {
    headline: 'Your sound is the draw. Your site should close the booking.',
    subHeadline:
      'Fugue Lab builds websites for gigging ensembles that put your audio, availability, and inquiry form in one place so a coordinator can go from "I want this band" to "booked" without a DM thread.',
    featuredSlug: null,
    cards: [
      { icon: Mic, title: 'Audio Up Front', desc: 'Sample reel, setlist, and video right where bookers look first.' },
      {
        icon: Calendar,
        title: 'Live Availability',
        desc: 'Calendar visibility so clients can check dates before reaching out.',
      },
      { icon: Zap, title: 'Instant Inquiry', desc: 'One form, all the details you need to quote a gig.' },
    ],
  },
  // Festival
  'music-festival': {
    headline: 'Applications, schedules, and sponsors. One front door.',
    subHeadline:
      'Fugue Lab builds websites for music festivals that connect your application pipeline, multi-venue schedules, and sponsor visibility into one cohesive experience.',
    featuredSlug: 'chroma-music-festival',
    cards: DEFAULT_CARDS,
  },
  'opera-company': {
    headline: 'Your season, your cast, your donors. One front door.',
    subHeadline:
      'Fugue Lab builds websites for opera companies that connect season ticketing, cast pages, and donor platforms into one professional presence.',
    featuredSlug: 'chroma-music-festival',
    cards: DEFAULT_CARDS,
  },
  // Education
  'university-music-dept': {
    headline: 'Your students find you online first. Make that moment count.',
    subHeadline:
      'Fugue Lab builds websites for university music departments that connect audition portals, recital calendars, and faculty pages into one place prospective students can explore.',
    featuredSlug: 'sierra-canyon-photo-competition',
    cards: DEFAULT_CARDS,
  },
  'music-school': {
    headline: 'Your students find you online first. Make that moment count.',
    subHeadline:
      'Fugue Lab builds websites for music schools that connect enrollment forms, lesson scheduling, and recital calendars so new families can register at any hour.',
    featuredSlug: 'sierra-canyon-photo-competition',
    cards: DEFAULT_CARDS,
  },
  'piano-studio': {
    headline: 'Your students find you online first. Make that moment count.',
    subHeadline:
      'Fugue Lab builds websites for music teachers that connect scheduling, enrollment, and recital information so studio admin stops eating your evenings.',
    featuredSlug: 'sierra-canyon-photo-competition',
    cards: DEFAULT_CARDS,
  },
  // Performer
  'wedding-musician': {
    headline: 'Your talent books itself. Your site should too.',
    subHeadline:
      'Fugue Lab builds bookable websites for wedding musicians. Sample audio, availability calendar, and instant inquiry form so a bride searching Saturday night can confirm by Monday.',
    featuredSlug: null,
    cards: [
      {
        icon: Mic,
        title: 'Sample Audio',
        desc: 'Couples hear you before they reach out. Audio and video front and center.',
      },
      {
        icon: Calendar,
        title: 'Availability Calendar',
        desc: 'Planners check your open dates without a back-and-forth thread.',
      },
      {
        icon: Zap,
        title: 'Instant Inquiry',
        desc: 'One form with date, venue, and ceremony details. No loose email threads.',
      },
    ],
  },
}

const DEFAULT_CONTENT: PersonalizedContent = {
  headline: 'Your music deserves a website that works as hard as you do.',
  subHeadline:
    'Fugue Lab builds websites and digital systems for classical music organizations. We connect the tools you already use into one professional front door for your audience. Built by a working organist and choirmaster who understands your world.',
  featuredSlug: null,
  cards: DEFAULT_CARDS,
}

function usePersonalizedContent(): PersonalizedContent {
  return useMemo(() => {
    const slug = getUtmContentSlug()
    if (!slug) return DEFAULT_CONTENT
    return CONTENT_GROUPS[slug] ?? DEFAULT_CONTENT
  }, [])
}

// ---------------------------------------------------------------------------
// Case studies & testimonials
// ---------------------------------------------------------------------------

const ALL_CASE_STUDIES = [
  {
    slug: 'chroma-music-festival',
    title: 'Chroma International Music Festival',
    type: 'Festival',
    result: 'Participants from 5+ countries',
    image: '/portfolio/chroma-festival.jpeg',
  },
  {
    slug: 'holy-trinity-covina',
    title: 'Holy Trinity Church',
    type: 'Church Music',
    result: '85% less admin time on music planning',
    image: '/portfolio/holy-trinity.jpeg',
  },
  {
    slug: 'sierra-canyon-photo-competition',
    title: 'Sierra Canyon School',
    type: 'Education',
    result: 'Paper process to digital in 2 weeks',
    image: '/portfolio/sierra-canyon-photo.png',
  },
]

const testimonials = [
  {
    quote:
      'Garrett understood exactly what we needed as a new festival. The site gave us instant credibility with sponsors, faculty, and prospective participants.',
    author: 'Shuai Wang',
    role: 'Artistic Director, Chroma Festival',
  },
  {
    quote:
      'Garrett did such a great job making a user-friendly submission and voting app for our photo competition. It was accessible for all ages and easy to use! He was helpful every step of the way and implemented feedback flawlessly.',
    author: 'Sierra Canyon Visual Arts Department',
    role: 'Art Teacher, Sierra Canyon School',
  },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function WelcomePage() {
  const justPaid = useJustPaid()

  useSEO({
    title: 'Welcome | Fugue Lab',
    description: 'Websites and digital systems for music organizations. Built by a musician and software engineer.',
    url: '/welcome',
  })

  const calendlyUrl = useCalendlyUrl()
  const content = usePersonalizedContent()
  const getStartedUrl = useMemo(() => buildGetStartedUrl(), [])

  // Reorder case studies to feature the relevant one first
  const caseStudies = useMemo(() => {
    if (!content.featuredSlug) return ALL_CASE_STUDIES
    const featured = ALL_CASE_STUDIES.find((cs) => cs.slug === content.featuredSlug)
    if (!featured) return ALL_CASE_STUDIES
    return [featured, ...ALL_CASE_STUDIES.filter((cs) => cs.slug !== content.featuredSlug)]
  }, [content.featuredSlug])

  // Post-payment success: a brand-new client just paid their deposit. Show the
  // "you're in, here's what happens next" state instead of the outreach landing.
  if (justPaid) return <PaidWelcome />

  return (
    <div className="bg-background min-h-screen">
      {/* Minimal header */}
      <header className="border-border/50 border-b px-6 py-4">
        <Link to="/" className="text-lg font-semibold tracking-tight">
          Fugue Lab
        </Link>
      </header>

      <main className="mx-auto max-w-3xl space-y-16 px-6 py-16">
        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-6"
        >
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{content.headline}</h1>
          <p className="text-muted-foreground text-lg leading-relaxed">{content.subHeadline}</p>

          {/* Primary CTA: Strategy call */}
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button size="lg" asChild>
              <a
                href={calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCalendlyClick('welcome_hero')}
              >
                Book a Free 30-Min Strategy Call <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button variant="ghost" size="lg" asChild>
              <Link to={getStartedUrl} onClick={() => trackGetStartedClick('welcome_hero')}>
                Tell Us About Your Project
              </Link>
            </Button>
          </div>
        </motion.section>

        {/* What we do */}
        <section className="grid gap-6 md:grid-cols-3">
          {content.cards.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="border-border/50 space-y-2 rounded-xl border p-5">
              <Icon className="text-primary h-5 w-5" />
              <h3 className="font-semibold">{title}</h3>
              <p className="text-muted-foreground text-sm">{desc}</p>
            </div>
          ))}
        </section>

        {/* Case studies */}
        <section className="space-y-6">
          <h2 className="text-xl font-semibold">Organizations we've helped</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {caseStudies.map((cs) => (
              <Link
                key={cs.slug}
                to={`/portfolio/${cs.slug}`}
                className="group border-border/50 hover:border-primary/30 overflow-hidden rounded-xl border transition-colors"
              >
                <div className="aspect-[3/2] overflow-hidden">
                  <img
                    src={cs.image}
                    alt={cs.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="space-y-1 p-4">
                  <p className="text-primary text-xs font-medium tracking-wider uppercase">{cs.type}</p>
                  <p className="group-hover:text-primary text-sm font-semibold transition-colors">{cs.title}</p>
                  <p className="text-muted-foreground text-xs">{cs.result}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section className="space-y-4">
          {testimonials.map((t, i) => (
            <blockquote key={i} className="border-primary/30 border-l-2 py-2 pl-4">
              <p className="text-muted-foreground text-sm italic">"{t.quote}"</p>
              <footer className="mt-1 text-xs font-medium">
                {t.author} <span className="text-muted-foreground">/ {t.role}</span>
              </footer>
            </blockquote>
          ))}
        </section>

        {/* Final CTA */}
        <section className="border-border/50 space-y-6 border-t py-8">
          <div className="space-y-2 text-center">
            <h2 className="text-xl font-semibold">Let's talk about your site</h2>
            <p className="text-muted-foreground">30 minutes. No pitch, just practical advice you can use right away.</p>
          </div>
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <Button size="lg" asChild>
              <a
                href={calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCalendlyClick('welcome_footer')}
              >
                Book a Free Strategy Call <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button variant="ghost" size="lg" asChild>
              <Link to={getStartedUrl} onClick={() => trackGetStartedClick('welcome_footer')}>
                Tell Us About Your Project
              </Link>
            </Button>
          </div>
        </section>
      </main>
    </div>
  )
}
